import React from 'react'
import { Control, Controller, UseFormSetValue } from 'react-hook-form'
import { InferType } from 'yup'
import { useQuery } from '@tanstack/react-query'
import { chatMessageSchema } from '../../../forms/schemas/chat_message'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { TextInput } from '../../common/TextInput'
import { DropdownSelect } from '../../common/DropdownSelect'
import { DropdownItemContent } from '../../feed/DropdownItemContent'
import { Avatar } from '../../common/Avatar'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../helpers/society'
import { useAuth } from '../../../hooks/useAuth'
import { societyQueries } from '../../../api/societies'

interface StartNewBoardChatHeaderProps {
  control: Control<InferType<typeof chatMessageSchema>>
  setValue: UseFormSetValue<InferType<typeof chatMessageSchema>>
}

export const StartNewBoardChatHeader = ({
  control,
  setValue,
}: StartNewBoardChatHeaderProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId: authUserId } = useAuth()
  const userId = authUserId as string
  const { data: societies } = useQuery(societyQueries.getUserSocieties(userId))

  const societyDropdownOptions =
    societies
      ?.filter((_society) => {
        return (
          isUserAdminInSociety(_society, userId) ||
          isUserBoardMemberInSociety(_society, userId)
        )
      })
      .map((society) => ({
        value: society._id,
        label: society.name,
        image: <Avatar size={30} url={society.coverPhoto?.url} />,
      })) || []

  return (
    <div className="my-4 mx-6 flex flex-col gap-2">
      <div className="flex w-full">
        <div>
          <div className="flex items-center">
            <div>{translate('startNewChat.boardChatInputLabel')}</div>
            <div className="w-60">
              <Controller
                control={control}
                name="groupChatTitle"
                render={({ field: { value, onChange, onBlur } }) => (
                  <TextInput
                    className="pl-2"
                    borderless
                    value={value}
                    onChange={(e) => {
                      // For some reason, onChange would not trigger in StartNewBoardChatHeader
                      // I've added a manual setValue for now.
                      setValue('groupChatTitle', e.currentTarget.value)
                      onChange(e)
                    }}
                    onBlur={onBlur}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-fit">
        <Controller
          control={control}
          name="society"
          render={({ field: { value, onChange } }) => (
            <DropdownSelect
              value={value}
              onChange={(e) => {
                // For some reason, onChange would not trigger in StartNewBoardChatHeader
                // I've added a manual setValue for now.
                onChange(e)
                setValue('society', e)
              }}
              options={societyDropdownOptions}
              dropdownWidth="w-60"
              renderItemContent={(option) => (
                <DropdownItemContent option={option} />
              )}
            />
          )}
        />
      </div>
    </div>
  )
}
